import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Megaphone, Plus, X, Edit2, Trash2, Bell, Paperclip, Download, FileText } from 'lucide-react';
import { api } from '../../services/api';

const createEmptyForm = () => ({
  titulo: '',
  descripcion: '',
  fecha: '',
  existingAttachments: [],
});

function normalizeAttachments(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (!item) return null;
      if (typeof item === 'string') {
        const segments = item.split('/');
        return {
          name: segments[segments.length - 1] || 'adjunto',
          path: item,
          url: item,
        };
      }
      const path = item.path || '';
      const url = item.url || path || '';
      const name = item.name || (path ? path.split('/').pop() : 'adjunto');
      if (!url && !path) return null;
      return { name, path, url };
    })
    .filter(Boolean);
}

function attachmentKey(item, index) {
  return `${item?.path || item?.url || item?.name || 'adjunto'}-${index}`;
}

export default function ComunicadosWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('comunicados');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(createEmptyForm);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [comunicados, setComunicados] = useState([]);
  const [campanas, setCampanas] = useState([]);
  const [loading, setLoading] = useState(false);

  const items = activeTab === 'comunicados' ? comunicados : campanas;
  const totalItems = comunicados.length + campanas.length;

  const resetForm = useCallback(() => {
    setShowForm(false);
    setEditingId(null);
    setFormData(createEmptyForm());
    setSelectedFiles([]);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.comunicados.get();
      if (res && res.ok) {
        const all = res.data || [];
        setComunicados(all.filter((i) => i.type === 'comunicado'));
        setCampanas(all.filter((i) => i.type === 'campana'));
        return all;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
    return [];
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  const handleSubmit = async () => {
    if (!formData.titulo || !formData.fecha) {
      alert('Por favor, complete el título y la fecha para guardar.');
      return;
    }

    const payload = new FormData();
    payload.append('titulo', formData.titulo);
    payload.append('descripcion', formData.descripcion || '');
    payload.append('fecha', formData.fecha);
    payload.append('type', activeTab === 'comunicados' ? 'comunicado' : 'campana');
    payload.append(
      'existing_attachments',
      JSON.stringify((formData.existingAttachments || []).map((item) => item.path || item.url).filter(Boolean))
    );
    if (editingId) payload.append('id', String(editingId));
    selectedFiles.forEach((file) => payload.append('files[]', file));

    setLoading(true);
    try {
      const res = await api.comunicados.upsert(payload);
      if (res && res.ok) {
        alert('Guardado correctamente');
        await fetchData();
        resetForm();
      } else {
        alert(`Error al guardar: ${res?.error || 'La respuesta del servidor no fue exitosa.'}`);
      }
    } catch (e) {
      console.error('Error en handleSubmit:', e);
      alert('Error al guardar: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setActiveTab(item.type === 'campana' ? 'campanas' : 'comunicados');
    setFormData({
      titulo: item.titulo || '',
      descripcion: item.descripcion || '',
      fecha: item.fecha || '',
      existingAttachments: normalizeAttachments(item.attachments),
    });
    setSelectedFiles([]);
    setEditingId(item.id);
    setShowForm(true);
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Seguro que desea eliminar?')) return;
    try {
      const res = await api.comunicados.delete({ id });
      if (res && res.ok) {
        await fetchData();
      } else {
        alert('Error al eliminar');
      }
    } catch (e) {
      console.error(e);
      alert('Error al eliminar: ' + e.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const date = new Date(parts[0], parts[1] - 1, parts[2]);
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
    }
    return dateString;
  };

  const handleClose = () => {
    setIsOpen(false);
    resetForm();
  };

  const handleFilesChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setSelectedFiles((prev) => [...prev, ...files]);
    event.target.value = '';
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const removeExistingAttachment = (index) => {
    setFormData((prev) => ({
      ...prev,
      existingAttachments: (prev.existingAttachments || []).filter((_, idx) => idx !== index),
    }));
  };

  const renderAttachments = (attachmentItems, compact = false) => {
    if (!Array.isArray(attachmentItems) || attachmentItems.length === 0) return null;
    return (
      <div className={`flex flex-wrap gap-2 ${compact ? 'mt-2' : 'mt-3'}`}>
        {attachmentItems.map((attachment, index) => (
          <a
            key={attachmentKey(attachment, index)}
            href={attachment.url || attachment.path}
            target="_blank"
            rel="noreferrer"
            className="inline-flex max-w-full items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700 hover:bg-orange-100"
          >
            <Paperclip className="h-3.5 w-3.5 shrink-0" />
            <span className="max-w-[220px] truncate sm:max-w-[320px]">{attachment.name}</span>
            <Download className="h-3.5 w-3.5 shrink-0" />
          </a>
        ))}
      </div>
    );
  };

  return (
    <div className="w-auto p-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 sm:gap-3 px-2.5 sm:px-3 py-2 text-white rounded-lg font-medium hover:opacity-90 transition-all shadow-lg whitespace-nowrap"
        style={{ backgroundColor: '#04873b' }}
      >
        <Bell className="w-5 h-5 shrink-0" />
        <span className="hidden sm:inline">Comunicados & Campañas</span>
        <span className="ml-1 sm:ml-2 px-2 py-1 bg-white text-xs rounded-full font-bold shrink-0" style={{ color: '#04873b' }}>
          {totalItems}
        </span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={handleClose}
          />
          <div className="fixed inset-y-0 right-0 w-full max-w-2xl bg-white shadow-2xl z-50 overflow-y-auto">
            <div className="bg-white rounded-l-2xl h-full flex flex-col">
              <div className="p-6 text-white" style={{ background: 'linear-gradient(135deg, #f07e2d 0%, #e36a1a 100%)' }}>
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Comunicados & Campañas</h2>
                    <p className="text-orange-100">Gestiona tus anuncios del mes</p>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('comunicados')}
                  className={`flex-1 py-4 px-6 font-medium transition-colors ${activeTab === 'comunicados' ? 'border-b-2 border-orange-50' : 'text-gray-600 hover:bg-gray-50'}`}
                  style={activeTab === 'comunicados' ? { color: '#f07e2d', borderBottomColor: '#f07e2d' } : {}}
                >
                  <Megaphone className="inline-block w-5 h-5 mr-2" />
                  Comunicados ({comunicados.length})
                </button>
                <button
                  onClick={() => setActiveTab('campanas')}
                  className={`flex-1 py-4 px-6 font-medium transition-colors ${activeTab === 'campanas' ? 'border-b-2 border-orange-50' : 'text-gray-600 hover:bg-gray-50'}`}
                  style={activeTab === 'campanas' ? { color: '#f07e2d', borderBottomColor: '#f07e2d' } : {}}
                >
                  <Calendar className="inline-block w-5 h-5 mr-2" />
                  Campañas ({campanas.length})
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {!showForm && (
                  <button
                    onClick={() => {
                      setEditingId(null);
                      setFormData(createEmptyForm());
                      setSelectedFiles([]);
                      setShowForm(true);
                    }}
                    className="w-full mb-4 py-3 px-4 text-white rounded-lg font-medium hover:opacity-90 transition-all flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(135deg, #f07e2d 0%, #e36a1a 100%)' }}
                  >
                    <Plus className="w-5 h-5" />
                    Agregar {activeTab === 'comunicados' ? 'Comunicado' : 'Campaña'}
                  </button>
                )}

                {showForm && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-gray-800">{editingId ? 'Editar' : 'Nuevo'} {activeTab === 'comunicados' ? 'Comunicado' : 'Campaña'}</h3>
                      <button
                        onClick={() => {
                          resetForm();
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Título"
                        value={formData.titulo}
                        onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        required
                      />
                      <textarea
                        placeholder="Descripción"
                        value={formData.descripcion}
                        onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
                        rows="3"
                      />
                      <input
                        type="date"
                        value={formData.fecha}
                        onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                        required
                      />
                      <div className="rounded-xl border border-dashed border-orange-200 bg-white p-3">
                        <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
                          <FileText className="h-4 w-4 text-orange-500" />
                          Adjuntar archivos
                        </label>
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.txt,.csv,.zip,.rar"
                          onChange={handleFilesChange}
                          className="w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-orange-100 file:px-3 file:py-2 file:font-medium file:text-orange-700 hover:file:bg-orange-200"
                        />
                        {!!formData.existingAttachments?.length && (
                          <div className="mt-3">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Adjuntos actuales</p>
                            <div className="flex flex-wrap gap-2">
                              {formData.existingAttachments.map((attachment, index) => (
                                <span
                                  key={attachmentKey(attachment, index)}
                                  className="inline-flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-medium text-green-700"
                                >
                                  <Paperclip className="h-3.5 w-3.5" />
                                  <span>{attachment.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeExistingAttachment(index)}
                                    className="rounded-full p-0.5 text-green-700 hover:bg-green-100"
                                    aria-label={`Quitar ${attachment.name}`}
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {!!selectedFiles.length && (
                          <div className="mt-3">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Archivos nuevos</p>
                            <div className="flex flex-wrap gap-2">
                              {selectedFiles.map((file, index) => (
                                <span
                                  key={`${file.name}-${file.size}-${index}`}
                                  className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700"
                                >
                                  <Paperclip className="h-3.5 w-3.5" />
                                  <span>{file.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeSelectedFile(index)}
                                    className="rounded-full p-0.5 text-orange-700 hover:bg-orange-100"
                                    aria-label={`Quitar ${file.name}`}
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="w-full py-2 text-white rounded-lg font-medium hover:opacity-90 transition-colors disabled:opacity-50"
                        style={{ backgroundColor: '#f07e2d' }}
                      >
                        {loading ? 'Guardando...' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                )}

                {loading && !showForm ? (
                  <div className="text-center py-4">Cargando...</div>
                ) : (
                  <ul className="space-y-3">
                    {items.map((item) => (
                      <li key={item.id} className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 text-xs rounded" style={{ backgroundColor: '#FFF3E8', color: '#F07E2D' }}>
                                {formatDate(item.fecha)}
                              </span>
                              <h4 className="font-semibold">{item.titulo}</h4>
                            </div>
                            {item.descripcion && (
                              <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{item.descripcion}</p>
                            )}
                            {renderAttachments(normalizeAttachments(item.attachments))}
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleEdit(item)} className="px-2 py-1 rounded bg-blue-600 text-white inline-flex items-center gap-1 text-xs"><Edit2 size={14} /> Editar</button>
                            <button onClick={() => handleDelete(item.id)} className="px-2 py-1 rounded bg-red-600 text-white inline-flex items-center gap-1 text-xs"><Trash2 size={14} /> Eliminar</button>
                          </div>
                        </div>
                      </li>
                    ))}
                    {items.length === 0 && (
                      <li className="p-6 text-center text-gray-600">Sin registros.</li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
